/*!
 * Quest One v2 dice (window.QODice)
 * Physical-feeling polyhedral dice drawn on a 2D canvas from real 3D geometry.
 * d20 = regular icosahedron, d6 = rounded cube with pips, plus d8 and d12.
 * Plain ES2017, no libraries, no WebGL, no network. Safe to load more than once.
 *
 *   var die = QODice.create(el, { sides: 20, skin: 'obsidian' });   // skins: 'obsidian', 'gold', 'mythic'
 *   die.roll(17).then(function (v) { ... });     // the caller decides the result
 *   QODice.rollMany(row, [{ sides: 6, result: 4 }, { sides: 6, result: 2 }]);
 */
(function (win) {
  'use strict';
  if (!win || win.QODice) return;
  var doc = win.document;

  var VERSION = '1.0.0';
  var PI = Math.PI, TAU = PI * 2, PHI = (1 + Math.sqrt(5)) / 2;
  var CAM = 7;          // camera distance in die radii (mild perspective)
  var FILL = 0.35;      // die radius as a share of the square box
  var BLEED = 0.3;      // canvas overhang on each side, as a share of the box
  var MAX_DPR = 2.5;
  var FONT_PX = 100;
  var FAMILY = "'Cinzel', 'Cinzel Decorative', 'Trajan Pro', Georgia, 'Times New Roman', serif";

  // Brand light: warm gold key from the upper left, cold moonlight rim, faint ember bounce.
  var C_GOLD = [201, 168, 76], C_HOT = [232, 198, 116], C_PALE = [244, 214, 138];
  var C_DEEP = [107, 83, 32], C_MOON = [159, 183, 217], C_EMBER = [224, 118, 79];
  var C_OBS = [20, 18, 28], C_INK = [27, 20, 9], C_RED = [239, 68, 68];
  // Mythic: a luminous die in FramedFantasy's brand purple and a pale teal, cut with dark violet numbers.
  var C_MYP = [239, 148, 255], C_MYT = [179, 245, 238], C_MYINK = [34, 14, 54];
  var SKINS = { obsidian: 1, gold: 1, mythic: 1 };
  function skinOf(s) { return SKINS[s] === 1 ? s : 'obsidian'; }

  // ---------------------------------------------------------------- math
  function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
  function addv(a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
  function mulv(a, s) { return [a[0] * s, a[1] * s, a[2] * s]; }
  function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
  function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
  function len(a) { return Math.sqrt(dot(a, a)); }
  function nrm(a) { var l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
  function clamp01(t) { return t < 0 ? 0 : t > 1 ? 1 : t; }
  function smooth(t) { t = clamp01(t); return t * t * (3 - 2 * t); }
  function easeOutCubic(t) { t = clamp01(t); var u = 1 - t; return 1 - u * u * u; }
  function easeInOutCubic(t) { t = clamp01(t); return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function mmul(A, B) {
    var C = new Array(9);
    for (var r = 0; r < 3; r++) {
      for (var c = 0; c < 3; c++) {
        C[r * 3 + c] = A[r * 3] * B[c] + A[r * 3 + 1] * B[3 + c] + A[r * 3 + 2] * B[6 + c];
      }
    }
    return C;
  }
  function mvec(M, v) {
    return [M[0] * v[0] + M[1] * v[1] + M[2] * v[2], M[3] * v[0] + M[4] * v[1] + M[5] * v[2], M[6] * v[0] + M[7] * v[1] + M[8] * v[2]];
  }
  function mtr(M) { return [M[0], M[3], M[6], M[1], M[4], M[7], M[2], M[5], M[8]]; }
  function rot(axis, ang) {
    var a = nrm(axis), x = a[0], y = a[1], z = a[2], c = Math.cos(ang), s = Math.sin(ang), t = 1 - c;
    return [t * x * x + c, t * x * y - s * z, t * x * z + s * y,
      t * x * y + s * z, t * y * y + c, t * y * z - s * x,
      t * x * z - s * y, t * y * z + s * x, t * z * z + c];
  }
  function toAxisAngle(M) {
    var tr = M[0] + M[4] + M[8];
    var ang = Math.acos(Math.max(-1, Math.min(1, (tr - 1) / 2)));
    if (ang < 1e-6) return { axis: [1, 0, 0], angle: 0 };
    if (PI - ang < 1e-3) {
      var xx = (M[0] + 1) / 2, yy = (M[4] + 1) / 2, zz = (M[8] + 1) / 2, ax;
      if (xx >= yy && xx >= zz) { var x = Math.sqrt(Math.max(xx, 1e-12)); ax = [x, (M[1] + M[3]) / (4 * x), (M[2] + M[6]) / (4 * x)]; }
      else if (yy >= zz) { var y = Math.sqrt(Math.max(yy, 1e-12)); ax = [(M[1] + M[3]) / (4 * y), y, (M[5] + M[7]) / (4 * y)]; }
      else { var z = Math.sqrt(Math.max(zz, 1e-12)); ax = [(M[2] + M[6]) / (4 * z), (M[5] + M[7]) / (4 * z), z]; }
      return { axis: nrm(ax), angle: ang };
    }
    var s = 2 * Math.sin(ang);
    return { axis: nrm([(M[7] - M[5]) / s, (M[2] - M[6]) / s, (M[3] - M[1]) / s]), angle: ang };
  }
  function rgb(c, k) {
    k = k === undefined ? 1 : k;
    var r = c[0] * k, g = c[1] * k, b = c[2] * k;
    return 'rgb(' + (r > 255 ? 255 : r < 0 ? 0 : r | 0) + ',' + (g > 255 ? 255 : g < 0 ? 0 : g | 0) + ',' + (b > 255 ? 255 : b < 0 ? 0 : b | 0) + ')';
  }
  function rgba(c, a) {
    return 'rgba(' + Math.min(255, c[0] | 0) + ',' + Math.min(255, c[1] | 0) + ',' + Math.min(255, c[2] | 0) + ',' + (a < 0 ? 0 : a > 1 ? 1 : a.toFixed(3)) + ')';
  }
  function mix(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
  function seeded(seed) {
    var s = (seed >>> 0) || 1;
    return function () {
      s = (s + 0x6D2B79F5) >>> 0;
      var t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function perf() { return (win.performance && win.performance.now) ? win.performance.now() : Date.now(); }
  function warn(msg) { try { if (win.console && win.console.warn) win.console.warn('[QODice] ' + msg); } catch (e) { /* ignore */ } }

  // ---------------------------------------------------------------- lighting
  var LK = nrm([-0.55, 0.78, 0.62]);   // warm key light, upper left, toward the viewer
  var LR = nrm([0.92, -0.1, -0.28]);   // cold moonlight rim from the right, slightly behind
  var LB = nrm([0.2, -1, 0.3]);        // faint ember bounce from the table
  var TILT_DIR = nrm([-0.7, 0.7, 0]);  // screen-space sheen direction for facet gradients

  // Gold never leaves the brand ramp: shadow brown -> deep -> gold -> hot -> pale -> warm white.
  var GOLD_RAMP = [[0, [30, 22, 9]], [0.45, C_DEEP], [0.95, C_GOLD], [1.35, C_HOT], [1.75, C_PALE], [2.4, [255, 246, 222]]];
  function ramp(R, k) {
    if (k <= R[0][0]) return R[0][1].slice();
    for (var i = 1; i < R.length; i++) {
      if (k <= R[i][0]) return mix(R[i - 1][1], R[i][1], (k - R[i - 1][0]) / (R[i][0] - R[i - 1][0]));
    }
    return R[R.length - 1][1].slice();
  }

  // Returns an [r,g,b] colour for a surface normal n seen from direction v.
  function shade(skin, n, v) {
    var ndv = Math.max(0, dot(n, v));
    var ndl = Math.max(0, dot(n, LK));
    var Rx = 2 * ndv * n[0] - v[0], Ry = 2 * ndv * n[1] - v[1], Rz = 2 * ndv * n[2] - v[2];
    var rl = Math.max(0, Rx * LK[0] + Ry * LK[1] + Rz * LK[2]);
    var sky = clamp01((Ry + 0.3) / 1.15);
    var rim = Math.max(0, dot(n, LR)); rim *= rim;
    var bnc = Math.max(0, dot(n, LB));
    var out = [0, 0, 0], i;
    if (skin === 'gold') {
      // polished metal: colour comes mostly from what it reflects, tinted gold
      var soft = Math.pow(rl, 4), hot = Math.pow(rl, 36);
      out = ramp(GOLD_RAMP, 0.22 + 0.9 * ndl + 0.3 * sky + 0.8 * soft + 1.1 * hot);
      for (i = 0; i < 3; i++) out[i] += C_MOON[i] * rim * 0.26 + C_EMBER[i] * bnc * 0.1;
    } else if (skin === 'trim') {
      // gold inlay on the obsidian edges: bright metal, never dark
      var s2 = Math.pow(rl, 4);
      out = ramp(GOLD_RAMP, 0.72 + 0.6 * ndl + 0.2 * sky + 0.8 * s2);
      for (i = 0; i < 3; i++) out[i] += C_MOON[i] * rim * 0.2;
    } else if (skin === 'mythic' || skin === 'mythicTrim') {
      // lit from within, so no face falls dark: the hue slides from brand purple (down and away from the key)
      // to pale teal (up toward it), a milky glow gathers at the silhouette, and the key leaves a white glint
      var hue = smooth(0.46 + 1.25 * (n[1] * 0.8 - n[0] * 0.45)), base = mix(C_MYP, C_MYT, hue);
      var gl = Math.pow(rl, 40), sh = Math.pow(rl, 6), fr = Math.pow(1 - ndv, 2);
      var lum = skin === 'mythicTrim' ? 1.06 + 0.12 * ndl : 0.7 + 0.26 * ndl + 0.08 * sky;
      var wash = skin === 'mythicTrim' ? 0.45 : 0.04 + 0.26 * fr;
      for (i = 0; i < 3; i++) out[i] = (base[i] * (1 - wash) + [255, 246, 255][i] * wash) * lum + 255 * (gl * 0.8 + sh * 0.12) + C_MOON[i] * rim * 0.12;
    } else {
      // glossy black glass: near-black body, the look comes from reflections
      var spec = Math.pow(rl, 48), sheen = Math.pow(rl, 6);
      var fres = Math.pow(1 - ndv, 2.2);
      var k2 = 0.55 + 0.75 * ndl;
      var envK = 0.05 + 0.5 * fres, skyS = smooth(sky);
      for (i = 0; i < 3; i++) {
        var env = [14, 12, 16][i] + ([104, 94, 84][i] - [14, 12, 16][i]) * skyS;
        out[i] = C_OBS[i] * k2 + env * envK + 255 * spec * 0.85 + C_HOT[i] * sheen * 0.3 + C_MOON[i] * rim * 0.36 + C_EMBER[i] * bnc * 0.06;
      }
    }
    return out;
  }

  // ---------------------------------------------------------------- geometry
  function icoVerts() {
    var a = [], s = [-1, 1];
    for (var i = 0; i < 2; i++) {
      for (var j = 0; j < 2; j++) {
        a.push([0, s[i], s[j] * PHI]);
        a.push([s[i], s[j] * PHI, 0]);
        a.push([s[j] * PHI, 0, s[i]]);
      }
    }
    return a.map(nrm);
  }
  function dodVerts() {
    var a = [], s = [-1, 1], ip = 1 / PHI, i, j, k;
    for (i = 0; i < 2; i++) for (j = 0; j < 2; j++) for (k = 0; k < 2; k++) a.push([s[i], s[j], s[k]]);
    for (i = 0; i < 2; i++) {
      for (j = 0; j < 2; j++) {
        a.push([0, s[i] * ip, s[j] * PHI]);
        a.push([s[i] * ip, s[j] * PHI, 0]);
        a.push([s[i] * PHI, 0, s[j] * ip]);
      }
    }
    return a.map(nrm);
  }
  function octVerts() { return [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]]; }
  function cubeVerts() {
    var a = [], s = [-1, 1];
    for (var i = 0; i < 2; i++) for (var j = 0; j < 2; j++) for (var k = 0; k < 2; k++) a.push([s[i], s[j], s[k]]);
    return a;
  }

  // Face planes of a convex, centred vertex set (every regular solid qualifies).
  function findFaces(V) {
    var faces = [], n = V.length, i, j, k, m;
    for (i = 0; i < n; i++) {
      for (j = i + 1; j < n; j++) {
        for (k = j + 1; k < n; k++) {
          var nn = cross(sub(V[j], V[i]), sub(V[k], V[i]));
          if (len(nn) < 1e-9) continue;
          nn = nrm(nn);
          var d = dot(nn, V[i]);
          if (d < 0) { nn = mulv(nn, -1); d = -d; }
          if (d < 1e-6) continue;
          var ok = true;
          for (m = 0; m < n && ok; m++) if (dot(nn, V[m]) > d + 1e-6) ok = false;
          if (!ok) continue;
          var dup = false;
          for (m = 0; m < faces.length; m++) if (dot(faces[m].n, nn) > 0.99999) { dup = true; break; }
          if (dup) continue;
          var idx = [];
          for (m = 0; m < n; m++) if (Math.abs(dot(nn, V[m]) - d) < 1e-6) idx.push(m);
          faces.push({ idx: idx, n: nn });
        }
      }
    }
    faces.forEach(function (f) {
      var c = [0, 0, 0];
      f.idx.forEach(function (q) { c = addv(c, V[q]); });
      c = mulv(c, 1 / f.idx.length);
      var e1 = nrm(sub(V[f.idx[0]], c)), e2 = cross(f.n, e1);
      f.idx.sort(function (a, b) {
        var pa = sub(V[a], c), pb = sub(V[b], c);
        return Math.atan2(dot(pa, e2), dot(pa, e1)) - Math.atan2(dot(pb, e2), dot(pb, e1));
      });
      f.c = c;
    });
    return faces;
  }

  // Value order: opposite faces always sum to n + 1. The d20 puts 2, 8, 14 around the 20 like a table die.
  var VALUE_SEQ = {
    20: [20, 2, 8, 14, 17, 5, 11, 3, 15, 9, 18, 4, 16, 6, 12, 10, 19, 1, 13, 7],
    12: [12, 2, 4, 6, 8, 10, 11, 3, 5, 7, 9, 1],
    8: [8, 2, 4, 6, 1, 3, 5, 7],
    6: [1, 2, 3, 4, 5, 6]
  };
  var SOLID_SCALE = { 20: 1.0, 12: 1.0, 8: 1.12, 6: 0.7 };

  var SOLIDS = {};
  function getSolid(sides) {
    if (SOLIDS[sides]) return SOLIDS[sides];
    var V = sides === 20 ? icoVerts() : sides === 12 ? dodVerts() : sides === 8 ? octVerts() : cubeVerts();
    var F = findFaces(V), nF = F.length, i, j, k;
    F.forEach(function (f) {
      var target = f.idx.length === 4 ? mulv(addv(V[f.idx[0]], V[f.idx[1]]), 0.5) : V[f.idx[0]];
      f.up = nrm(sub(target, f.c));
      f.right = cross(f.up, f.n);
      var ir = 1e9;
      for (k = 0; k < f.idx.length; k++) {
        var a = V[f.idx[k]], b = V[f.idx[(k + 1) % f.idx.length]];
        var ab = nrm(sub(b, a)), ac = sub(f.c, a);
        var perp = sub(ac, mulv(ab, dot(ac, ab)));
        ir = Math.min(ir, len(perp));
      }
      f.inr = ir;
      f.value = 0;
    });
    // adjacency and opposites
    var edges = [], adj = [];
    for (i = 0; i < nF; i++) adj.push([]);
    for (i = 0; i < nF; i++) {
      for (j = i + 1; j < nF; j++) {
        var shared = F[i].idx.filter(function (q) { return F[j].idx.indexOf(q) >= 0; });
        if (shared.length === 2) { adj[i].push(j); adj[j].push(i); edges.push({ a: shared[0], b: shared[1], f1: i, f2: j }); }
      }
      for (j = 0; j < nF; j++) if (dot(F[i].n, F[j].n) < -0.9999) F[i].opp = j;
    }
    // numbering by breadth-first order from face 0
    var seq = VALUE_SEQ[sides], used = {}, order = [0], seen = { 0: true };
    for (i = 0; i < order.length; i++) {
      adj[order[i]].forEach(function (q) { if (!seen[q]) { seen[q] = true; order.push(q); } });
    }
    order.forEach(function (fi) {
      if (F[fi].value) return;
      for (var s = 0; s < seq.length; s++) {
        var v = seq[s];
        if (!used[v] && !used[nF + 1 - v]) {
          F[fi].value = v; used[v] = true;
          F[F[fi].opp].value = nF + 1 - v; used[nF + 1 - v] = true;
          return;
        }
      }
    });
    var byValue = {};
    F.forEach(function (f, fi) { byValue[f.value] = fi; });
    var solid = {
      sides: sides, n: nF, V: V, F: F, edges: edges, byValue: byValue,
      scale: SOLID_SCALE[sides] || 1, kind: sides === 6 ? 'rcube' : 'poly', labels: null
    };
    if (sides === 6) solid.mesh = buildRoundedCube(3, 0.24);
    SOLIDS[sides] = solid;
    return solid;
  }

  // Rounded cube (Minkowski sum of a box and a sphere) as a quad mesh with even angular steps on the rounding.
  function buildRoundedCube(K, rr) {
    var inner = 1 - rr, S = [], k, i, j;
    for (k = K; k >= 0; k--) S.push(-(inner + rr * Math.tan(k / K * PI / 4)));
    for (k = 0; k <= K; k++) S.push(inner + rr * Math.tan(k / K * PI / 4));
    var quads = [];
    function cl(x) { return x < -inner ? -inner : x > inner ? inner : x; }
    for (var ax = 0; ax < 3; ax++) {
      for (var sg = -1; sg <= 1; sg += 2) {
        var u = (ax + 1) % 3, v = (ax + 2) % 3, grid = [];
        for (i = 0; i < S.length; i++) {
          grid.push([]);
          for (j = 0; j < S.length; j++) {
            var p = [0, 0, 0]; p[ax] = sg; p[u] = S[i]; p[v] = S[j];
            var q = [cl(p[0]), cl(p[1]), cl(p[2])];
            var nn = nrm(sub(p, q));
            grid[i].push({ p: addv(q, mulv(nn, rr)), n: nn });
          }
        }
        for (i = 0; i < S.length - 1; i++) {
          for (j = 0; j < S.length - 1; j++) {
            var g = [grid[i][j], grid[i + 1][j], grid[i + 1][j + 1], grid[i][j + 1]];
            var na = nrm(addv(addv(g[0].n, g[1].n), addv(g[2].n, g[3].n)));
            var cN = cross(sub(g[1].p, g[0].p), sub(g[3].p, g[0].p));
            if (dot(cN, na) < 0) g.reverse();
            var pts = g.map(function (o) { return o.p; }), vn = g.map(function (o) { return o.n; });
            var cen = mulv(addv(addv(pts[0], pts[1]), addv(pts[2], pts[3])), 0.25);
            // shading varies across the rounding: along 0->1 or along 0->3
            var d01 = len(sub(vn[0], vn[1])) + len(sub(vn[3], vn[2])), d03 = len(sub(vn[0], vn[3])) + len(sub(vn[1], vn[2]));
            quads.push({ p: pts, vn: vn, n: na, c: cen, flat: i === K && j === K, along01: d01 >= d03 });
          }
        }
      }
    }
    return { quads: quads, inner: inner };
  }

  // Rotation that puts face f straight at the viewer (+z) with its up vector pointing up (+y).
  function poseMatrix(f) {
    var r = f.right, u = f.up, n = f.n;
    return [r[0], r[1], r[2], u[0], u[1], u[2], n[0], n[1], n[2]];
  }

  // ---------------------------------------------------------------- roll timing (normalised 0..1)
  var IMP = [0.34, 0.56, 0.70, 0.78];      // table impacts
  var HGT = [1, 0.3, 0.1, 0.028];          // bounce heights (share of the throw height)
  var SEG_END = [0.34, 0.56, 0.70, 0.80];  // spin slows at each impact
  var SPW = [1, 0.6, 0.34, 0.18];
  var TS = 0.8;                            // settle phase starts (last ~20 %, ~260 ms at 1300 ms)
  var A_SET = SPW[3] * (1 - TS) / 2;
  var SPIN_TOTAL = (function () {
    var s = 0, a = 0;
    for (var i = 0; i < 4; i++) { s += (SEG_END[i] - a) * SPW[i]; a = SEG_END[i]; }
    return s + A_SET;
  })();
  // Share of the spin still to go; dips slightly below zero during the settle (the overshoot).
  function spinRemain(t) {
    if (t >= 1) return 0;
    if (t >= TS) {
      var u = (t - TS) / (1 - TS), k = 1 - u;
      return A_SET * k * k * Math.cos(1.5 * PI * u) / SPIN_TOTAL;
    }
    var rem = A_SET, a = 0;
    for (var i = 0; i < 4; i++) {
      var b = SEG_END[i];
      if (t < b) rem += (b - Math.max(a, t)) * SPW[i];
      a = b;
    }
    return rem / SPIN_TOTAL;
  }
  function heightAt(t) {
    var a = 0;
    for (var i = 0; i < 4; i++) {
      var b = IMP[i];
      if (t < b) { var u = (t - a) / (b - a); return 4 * HGT[i] * u * (1 - u); }
      a = b;
    }
    return 0;
  }
  function driftAt(t) {
    if (t < IMP[0]) return Math.sin(PI / 2 * t / IMP[0]);
    return 1 - smooth((t - IMP[0]) / (TS - IMP[0]));
  }

  // ---------------------------------------------------------------- shared frame loop
  var live = [];
  var rafId = 0;
  var raf = win.requestAnimationFrame ? win.requestAnimationFrame.bind(win) : function (cb) { return win.setTimeout(function () { cb(perf()); }, 16); };
  var caf = win.cancelAnimationFrame ? win.cancelAnimationFrame.bind(win) : win.clearTimeout.bind(win);
  function wake(d) {
    if (d && live.indexOf(d) < 0) live.push(d);
    if (!rafId && !(doc && doc.hidden)) rafId = raf(tick);
  }
  function tick() {
    rafId = 0;
    var now = perf();
    var keep = [];
    for (var i = 0; i < live.length; i++) {
      var d = live[i], more = false;
      try { more = d._tick(now); } catch (e) { warn(e && e.message); more = false; }
      if (more) keep.push(d);
    }
    live = keep;
    if (live.length) wake();
  }
  if (doc && doc.addEventListener) {
    doc.addEventListener('visibilitychange', function () {
      if (doc.hidden) { if (rafId) { caf(rafId); rafId = 0; } }
      else if (live.length) wake();
    });
  }
  var fontHooked = false;
  function hookFonts() {
    if (fontHooked || !doc || !doc.fonts) return;
    fontHooked = true;
    var refresh = function () {
      Object.keys(SOLIDS).forEach(function (k) { SOLIDS[k].labels = null; });
      allDice.forEach(function (d) { try { d._redraw(); } catch (e) { /* ignore */ } });
    };
    try {
      if (doc.fonts.ready && doc.fonts.ready.then) doc.fonts.ready.then(refresh, function () {});
      if (doc.fonts.load) doc.fonts.load('700 40px Cinzel', '0123456789').then(refresh, function () {});
      if (doc.fonts.addEventListener) doc.fonts.addEventListener('loadingdone', refresh);
    } catch (e) { /* ignore */ }
  }
  var allDice = [];
  function reducedPref() {
    try { return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }

  // ---------------------------------------------------------------- the die
  function Die(el, o) {
    o = o || {};
    var sides = +o.sides || 20;
    if (!VALUE_SEQ[sides]) { warn('d' + sides + ' is not supported; drawing a d20'); sides = 20; }
    this.sides = sides;
    this.solid = getSolid(sides);
    this.skin = skinOf(o.skin);
    this.idleOn = o.idle !== false;
    this._reducedOpt = typeof o.reduced === 'boolean' ? o.reduced : null;
    this.critFx = typeof o.critFx === 'boolean' ? o.critFx : sides === 20;
    this.onLand = typeof o.onLand === 'function' ? o.onLand : null;
    this.onImpact = typeof o.onImpact === 'function' ? o.onImpact : null;
    this.duration = +o.duration > 0 ? +o.duration : 1300;
    this.bleed = typeof o.bleed === 'number' ? Math.max(0, Math.min(1, o.bleed)) : BLEED;
    this.size = +o.size > 0 ? +o.size : 0;
    this.throwHeight = +o.throwHeight > 0 ? +o.throwHeight : 0.62;   // in die radii
    // Display tilt (degrees): d20/d8/d12 face the viewer straight on; the d6 sits a little below
    // eye level and turned so its top and side read as a cube. The result face still leads.
    var tilt = typeof o.tilt === 'number' ? o.tilt : (sides === 6 ? 22 : 0);
    var turn = typeof o.turn === 'number' ? o.turn : (sides === 6 ? -16 : 0);
    this.tiltM = (tilt || turn) ? mmul(rot([1, 0, 0], tilt * PI / 180), rot([0, 1, 0], turn * PI / 180)) : null;
    this._rng = typeof o.seed === 'number' ? seeded(o.seed) : Math.random;
    this.value = this._clampValue(o.value !== undefined ? o.value : this.solid.n);
    this.Mf = this._poseFor(this.value);
    // resting floor = lowest point of the settled, tilted die (identical for every face by symmetry)
    var Mfl = this.tiltM ? mmul(this.tiltM, this.Mf) : this.Mf, lo = 0, sol = this.solid;
    var pts = sol.mesh ? [].concat.apply([], sol.mesh.quads.map(function (q) { return q.p; })) : sol.V;
    for (var pi = 0; pi < pts.length; pi++) lo = Math.min(lo, mvec(Mfl, pts[pi])[1]);
    this.floor = lo * sol.scale;
    this._held = false;
    this._idleT0 = perf();
    this._roll = null;
    this._fx = null;
    this._fade = null;
    this._dead = false;
    this._offscreen = false;
    this._lastDraw = 0;
    this._mount(el);
    allDice.push(this);
    hookFonts();
    try { this._layout(); } catch (e) { warn('layout failed: ' + (e && e.message)); }
    if (this.idleOn && !this._reduced()) wake(this);
  }

  Die.prototype._reduced = function () { return this._reducedOpt !== null ? this._reducedOpt : reducedPref(); };
  Die.prototype._clampValue = function (v) {
    v = Math.round(+v);
    if (!(v >= 1)) v = 1;
    if (v > this.solid.n) v = this.solid.n;
    return v;
  };
  Die.prototype._poseFor = function (v) { return poseMatrix(this.solid.F[this.solid.byValue[v]]); };

  Die.prototype._mount = function (el) {
    var self = this;
    this.host = el;
    if (el && el.tagName && el.tagName.toUpperCase() === 'CANVAS') {
      this.canvas = el; this.wrap = null; this.ownCanvas = false; this.bleed = 0;
    } else {
      var wrap = doc.createElement('div');
      wrap.className = 'qod-die';
      wrap.style.cssText = 'position:relative;width:100%;height:100%;pointer-events:none;';
      var cv = doc.createElement('canvas');
      cv.style.cssText = 'position:absolute;left:0;top:0;display:block;pointer-events:none;';
      wrap.appendChild(cv);
      if (el && el.appendChild) el.appendChild(wrap);
      this.wrap = wrap; this.canvas = cv; this.ownCanvas = true;
      this._autoHeight = !(el && el.clientHeight > 0);
    }
    this.canvas.setAttribute('role', 'img');
    this.canvas.setAttribute('aria-label', 'd' + this.sides + ' showing ' + this.value);
    this.ctx = this.canvas.getContext ? this.canvas.getContext('2d') : null;
    var onResize = function () {
      if (self._dead) return;
      try { self._layout(); } catch (e) { warn('layout failed: ' + (e && e.message)); }
    };
    if (win.ResizeObserver) {
      try { this._ro = new win.ResizeObserver(onResize); this._ro.observe(this.wrap || this.canvas); } catch (e) { this._ro = null; }
    }
    if (!this._ro) { this._onWinResize = onResize; win.addEventListener('resize', onResize); }
    if (win.IntersectionObserver) {
      try {
        this._io = new win.IntersectionObserver(function (entries) {
          var e = entries[entries.length - 1];
          self._offscreen = !e.isIntersecting;
          if (!self._offscreen) wake(self);
        });
        this._io.observe(this.wrap || this.canvas);
      } catch (e2) { this._io = null; }
    }
  };

  Die.prototype._layout = function () {
    if (!this.ctx) return;
    var dpr = Math.min(win.devicePixelRatio || 1, MAX_DPR);
    var S, cw, ch, C;
    if (this.ownCanvas) {
      cw = this.wrap.clientWidth;
      if (this._autoHeight) {
        S = this.size || cw;
        if (S > 0) this.wrap.style.height = S + 'px';
        ch = S;
      } else {
        ch = this.wrap.clientHeight;
        if (!(ch > 0)) {
          // height:100% of an auto-height parent resolves to 0: size from the width instead
          this._autoHeight = true;
          return this._layout();
        }
        S = this.size || Math.min(cw, ch);
      }
      if (!(S > 0)) return;
      C = S * (1 + 2 * this.bleed);
      var cs = this.canvas.style;
      cs.width = C + 'px'; cs.height = C + 'px';
      cs.left = ((cw - C) / 2) + 'px'; cs.top = ((ch - C) / 2) + 'px';
    } else {
      cw = this.canvas.clientWidth || this.canvas.width;
      ch = this.canvas.clientHeight || this.canvas.height;
      S = Math.min(cw, ch);
      if (!(S > 0)) return;
      C = S;
    }
    var W = Math.max(1, Math.round(C * dpr));
    var H = this.ownCanvas ? W : Math.max(1, Math.round(ch * dpr));
    var Wd = this.ownCanvas ? W : Math.max(1, Math.round(cw * dpr));
    if (this.canvas.width !== Wd) this.canvas.width = Wd;
    if (this.canvas.height !== H) this.canvas.height = H;
    this.W = Wd; this.H = H; this.dpr = dpr; this.S = S;
    this.R = S * FILL * dpr;                 // die radius in device px
    this.fpx = this.R * CAM;
    this.cx = Wd / 2;
    this.cy = H / 2 - S * 0.035 * dpr;
    this._redraw();
  };

  Die.prototype._redraw = function () {
    if (this._dead || !this.W) return;
    this._draw(perf());
  };

  // ---------------------------------------------------------------- poses
  Die.prototype._restPose = function (now) {
    if (this.idleOn && !this._held && !this._reduced()) {
      var s = (now - this._idleT0) / 1000, ramp = smooth(s / 0.9);
      var a = 0.17 * Math.sin(s * 0.85) * ramp, b = 0.1 * Math.sin(s * 0.63 + 1.1) * ramp;
      return { M: mmul(rot([0, 1, 0], a), mmul(rot([1, 0, 0], b), this.Mf)), x: 0, lift: (0.08 + 0.05 * Math.sin(s * 1.3)) * ramp, z: 0 };
    }
    return { M: this.Mf, x: 0, lift: 0, z: 0 };
  };

  Die.prototype._initRoll = function (R, now) {
    var cur = this._restPose(now);
    var rnd = R.rng;
    var D = mmul(cur.M, mtr(R.Mf)), aa = toAxisAngle(D);
    var dir = rnd() < 0.5 ? -1 : 1;
    R.M0 = cur.M; R.lift0 = cur.lift;
    R.dAx = aa.axis; R.dAng = aa.angle;
    R.axS = nrm([-1, (rnd() - 0.5) * 0.5, 0.55 * dir]);
    R.spin = TAU * (this.sides === 6 ? 2 : 3);
    R.axW = nrm([(rnd() - 0.5) * 0.7, 1, (rnd() - 0.5) * 0.7]);
    R.wob = TAU * (rnd() < 0.5 ? -1 : 1);
    R.drift = dir * (0.16 + rnd() * 0.14);
    R.H = this.throwHeight * (0.9 + rnd() * 0.2);
    R.zAmp = 0.4;
    R.init = true;
  };

  Die.prototype._rollPose = function (R, t) {
    var M = mmul(rot(R.dAx, R.dAng * (1 - easeInOutCubic(t / 0.62))), R.Mf);
    M = mmul(rot(R.axW, R.wob * (1 - easeOutCubic(t / 0.72))), M);
    M = mmul(rot(R.axS, R.spin * spinRemain(t)), M);
    return {
      M: M,
      x: R.drift * driftAt(t),
      lift: heightAt(t) * R.H + R.lift0 * (1 - smooth(t / 0.18)),
      z: R.zAmp * Math.sin(PI * clamp01(t / IMP[0]))
    };
  };

  Die.prototype._pose = function (now) {
    var R = this._roll;
    if (R && !R.reduced && now >= R.t0) {
      if (!R.init) this._initRoll(R, now);
      return this._rollPose(R, clamp01((now - R.t0) / R.dur));
    }
    if (R && R.reduced) return { M: R.Mf, x: 0, lift: 0, z: 0 };
    return this._restPose(now);
  };

  // ---------------------------------------------------------------- frame loop hook
  Die.prototype._tick = function (now) {
    if (this._dead || this._frozen) return false;
    var busy = false, R = this._roll;
    if (R) {
      busy = true;
      if (now >= R.t0) {
        var t = (now - R.t0) / R.dur;
        if (!R.reduced) {
          while (R.imp < IMP.length && t >= IMP[R.imp]) {
            var k = R.imp++;
            if (this.onImpact && t - IMP[k] < 0.08) {
              try { this.onImpact(k, [1, 0.55, 0.3, 0.12][k]); } catch (e) { warn('onImpact threw'); }
            }
          }
        }
        if (t >= 1) { this._land(now); R = null; }
      }
    }
    if (this._fade) {
      var ft = (now - this._fade.t0) / this._fade.dur;
      this.canvas.style.opacity = String(clamp01(ft));
      if (ft >= 1) { this._fade = null; this.canvas.style.opacity = ''; } else busy = true;
    }
    if (this._fx) {
      if (now - this._fx.t0 > this._fx.dur) { this._fx = null; this._draw(now); }
      else busy = true;
    }
    var idling = this.idleOn && !this._held && !this._reduced();
    if (!busy && !idling) return false;
    if (this._offscreen && !busy) return false;
    if (!busy && now - this._lastDraw < 30) return true;   // idle hover runs at ~30 fps
    if (!this._offscreen) this._draw(now);
    return true;
  };

  Die.prototype._land = function (now) {
    var R = this._roll;
    if (!R) return;
    if (R.timer) { win.clearTimeout(R.timer); R.timer = 0; }
    this._roll = null;
    this.value = R.value;
    this.Mf = R.Mf;
    this._held = true;
    if (this.critFx && !R.noFx) {
      var red = R.reduced;
      if (R.value === this.solid.n) this._fx = { type: 'crit', t0: now, dur: red ? 700 : 900, reduced: red, seed: (R.rng() * 1e9) | 0 };
      else if (R.value === 1) this._fx = { type: 'fumble', t0: now, dur: red ? 450 : 560, reduced: red };
    }
    if (this._fx) wake(this);
    try { this.canvas.setAttribute('aria-label', 'd' + this.sides + ' showing ' + R.value); } catch (e) { /* ignore */ }
    this._draw(now);
    var cbs = [this.onLand, R.onLand];
    for (var i = 0; i < cbs.length; i++) {
      if (typeof cbs[i] === 'function') { try { cbs[i](R.value, this); } catch (e2) { warn('onLand threw'); } }
    }
    try { R.resolve(R.value); } catch (e3) { /* ignore */ }
  };

  // ---------------------------------------------------------------- drawing
  Die.prototype._project = function (w, out) {
    var d = CAM - w[2];
    if (d < 0.5) d = 0.5;
    out[0] = this.cx + this.fpx * w[0] / d;
    out[1] = this.cy - this.fpx * w[1] / d;
    return out;
  };

  Die.prototype._draw = function (now) {
    var ctx = this.ctx;
    if (!ctx || !this.W || this._dead) return;
    this._lastDraw = now;
    var fz = this._frozen;
    if (fz && fz.now !== undefined) now = fz.now;
    var P = fz && fz.P ? fz.P : this._pose(now);
    var fx = this._fx, el = fx ? now - fx.t0 : 0, fu = fx ? clamp01(el / fx.dur) : 0;
    var shakeX = 0;
    if (fx && fx.type === 'fumble' && !fx.reduced) shakeX = 0.035 * Math.sin(el * 0.11) * (1 - fu) * (1 - fu);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, this.W, this.H);
    var M = this.tiltM ? mmul(this.tiltM, P.M) : P.M;
    var T = [P.x + shakeX, P.lift, P.z];
    try {
      if (this.solid.kind === 'rcube') this._drawCube(ctx, M, T);
      else this._drawPoly(ctx, M, T);
    } catch (e) { warn('draw failed: ' + (e && e.message)); }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    var center = [0, 0];
    this._project([T[0], T[1], T[2]], center);
    var R = this.R * (CAM / (CAM - T[2]));
    // effects that tint the die itself
    if (fx) {
      ctx.globalCompositeOperation = 'source-atop';
      if (fx.type === 'crit') {
        var fa = fx.reduced ? 0 : Math.pow(1 - clamp01(el / 340), 2) * (this.skin === 'gold' ? 0.4 : 0.34) * (el < 40 ? 0.5 + el / 80 : 1);
        if (fa > 0.003) {
          var g = ctx.createRadialGradient(center[0] - R * 0.3, center[1] - R * 0.35, 0, center[0], center[1], R * 1.3);
          g.addColorStop(0, rgba([255, 240, 200], fa));
          g.addColorStop(0.6, rgba(C_HOT, fa * 0.55));
          g.addColorStop(1, rgba(C_GOLD, fa * 0.2));
          ctx.fillStyle = g;
          ctx.fillRect(0, 0, this.W, this.H);
        }
      } else {
        var ra = fx.reduced ? 0.42 * (1 - fu)
          : (0.55 * Math.abs(Math.sin(el * 0.034)) + 0.12) * (1 - fu) * (el < 60 ? el / 60 : 1);
        if (ra > 0.003) {
          ctx.fillStyle = rgba([70, 6, 8], ra * 0.7);
          ctx.fillRect(0, 0, this.W, this.H);
          ctx.fillStyle = rgba(C_RED, ra * 0.85);
          ctx.fillRect(0, 0, this.W, this.H);
        }
      }
    }
    // contact shadow behind the die
    ctx.globalCompositeOperation = 'destination-over';
    if (this.skin === 'mythic') this._drawAura(ctx, center, R);
    this._drawShadow(ctx, P.lift, T[0]);
    // front effects
    ctx.globalCompositeOperation = 'source-over';
    if (fx && fx.type === 'crit') this._drawCrit(ctx, center, R, el, fx);
    ctx.globalCompositeOperation = 'source-over';
  };

  Die.prototype._drawShadow = function (ctx, lift, x) {
    var floorY = this.cy - this.fpx * this.floor / CAM;
    var sx = this.cx + this.fpx * x / CAM;
    var h = clamp01(lift / 1.1);
    var R = this.R;
    var w = R * (0.95 + 0.6 * h), a = 0.6 * (1 - 0.62 * h);
    ctx.save();
    ctx.translate(sx, floorY);
    ctx.scale(1, 0.24);
    var g = ctx.createRadialGradient(0, 0, 0, 0, 0, w);
    g.addColorStop(0, 'rgba(0,0,0,' + a.toFixed(3) + ')');
    g.addColorStop(0.5, 'rgba(0,0,0,' + (a * 0.5).toFixed(3) + ')');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(-w, -w, 2 * w, 2 * w);
    var c = (1 - h) * (1 - h);
    if (c > 0.01) {
      var w2 = R * 0.62;
      var g2 = ctx.createRadialGradient(0, 0, 0, 0, 0, w2);
      g2.addColorStop(0, 'rgba(0,0,0,' + (0.55 * c).toFixed(3) + ')');
      g2.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g2;
      ctx.fillRect(-w2, -w2, 2 * w2, 2 * w2);
    }
    ctx.restore();
  };

  // Mythic only: a faint purple-to-teal glow behind the die, so it reads as giving off light
  Die.prototype._drawAura = function (ctx, c, R) {
    var g = ctx.createRadialGradient(c[0] - R * 0.2, c[1] - R * 0.25, R * 0.35, c[0], c[1], R * 1.42);
    g.addColorStop(0, rgba(C_MYT, 0.3));
    g.addColorStop(0.45, rgba(C_MYP, 0.16));
    g.addColorStop(1, rgba(C_MYP, 0));
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(c[0], c[1], R * 1.42, 0, TAU); ctx.fill();
  };

  Die.prototype._drawCrit = function (ctx, c, R, el, fx) {
    if (fx.reduced) {
      var ra = 0.8 * (1 - clamp01(el / fx.dur));
      ctx.lineWidth = R * 0.05;
      ctx.strokeStyle = rgba(C_PALE, ra);
      ctx.beginPath(); ctx.arc(c[0], c[1], R * 1.22, 0, TAU); ctx.stroke();
      return;
    }
    var u = clamp01(el / 680), rr = R * (0.96 + 0.66 * easeOutCubic(u)), a = Math.pow(1 - u, 1.5) * smooth(el / 50);
    if (a > 0.004) {
      ctx.lineWidth = R * (0.13 * (1 - u) + 0.03);
      ctx.strokeStyle = rgba(C_HOT, a * 0.28);
      ctx.beginPath(); ctx.arc(c[0], c[1], rr, 0, TAU); ctx.stroke();
      ctx.lineWidth = R * (0.05 * (1 - u) + 0.012);
      ctx.strokeStyle = rgba([255, 238, 190], a * 0.95);
      ctx.beginPath(); ctx.arc(c[0], c[1], rr, 0, TAU); ctx.stroke();
    }
    // sparks
    var rnd = seeded(fx.seed || 7), us = clamp01(el / 820);
    if (us < 1) {
      ctx.lineCap = 'round';
      for (var i = 0; i < 12; i++) {
        var ang = (i / 12) * TAU + (rnd() - 0.5) * 0.4, sp = 0.75 + rnd() * 0.55;
        var d1 = R * (0.95 + 0.95 * sp * easeOutCubic(us)), d2 = d1 + R * 0.16 * (1 - us);
        var sa = (1 - us) * (0.6 + 0.4 * rnd());
        ctx.strokeStyle = rgba(i % 3 ? C_HOT : [255, 240, 200], sa);
        ctx.lineWidth = R * 0.028 * (1 - us * 0.6);
        ctx.beginPath();
        ctx.moveTo(c[0] + Math.cos(ang) * d1, c[1] + Math.sin(ang) * d1 + R * 0.25 * us * us);
        ctx.lineTo(c[0] + Math.cos(ang) * d2, c[1] + Math.sin(ang) * d2 + R * 0.25 * us * us);
        ctx.stroke();
      }
    }
    // glint on the upper-left of the die
    var ug = clamp01(el / 520);
    if (ug < 1) {
      var gs = R * 0.34 * Math.sin(PI * ug), gx = c[0] - R * 0.42, gy = c[1] - R * 0.5;
      if (gs > 0.5) {
        ctx.fillStyle = rgba([255, 247, 225], 0.95 * Math.sin(PI * ug));
        ctx.beginPath();
        ctx.moveTo(gx, gy - gs); ctx.quadraticCurveTo(gx, gy, gx + gs, gy);
        ctx.quadraticCurveTo(gx, gy, gx, gy + gs); ctx.quadraticCurveTo(gx, gy, gx - gs, gy);
        ctx.quadraticCurveTo(gx, gy, gx, gy - gs); ctx.fill();
        var gg = ctx.createRadialGradient(gx, gy, 0, gx, gy, gs * 0.6);
        gg.addColorStop(0, rgba([255, 250, 235], 0.9 * Math.sin(PI * ug)));
        gg.addColorStop(1, rgba(C_PALE, 0));
        ctx.fillStyle = gg;
        ctx.beginPath(); ctx.arc(gx, gy, gs * 0.6, 0, TAU); ctx.fill();
      }
    }
  };

  // Screen-space affine frame of a face: origin at the face centre, unit = one solid unit.
  Die.prototype._faceFrame = function (f, M, T, sc, lower) {
    var c = addv(mulv(mvec(M, f.c), sc), T);
    var r = mulv(mvec(M, f.right), sc), u = mulv(mvec(M, f.up), sc);
    if (lower) c = sub(c, mulv(u, lower));
    var e = 0.04, p0 = [0, 0], px = [0, 0], py = [0, 0];
    this._project(c, p0);
    this._project(addv(c, mulv(r, e)), px);
    this._project(addv(c, mulv(u, e)), py);
    return { ox: p0[0], oy: p0[1], ax: (px[0] - p0[0]) / e, ay: (px[1] - p0[1]) / e, bx: (py[0] - p0[0]) / e, by: (py[1] - p0[1]) / e };
  };

  Die.prototype._labels = function (ctx) {
    var s = this.solid;
    if (s.labels) return s.labels;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.font = '700 ' + FONT_PX + 'px ' + FAMILY;
    var out = { byValue: {}, capH: 70, maxW: 1 };
    var z = ctx.measureText('0');
    var capH = z.actualBoundingBoxAscent ? z.actualBoundingBoxAscent + (z.actualBoundingBoxDescent || 0) : FONT_PX * 0.7;
    out.capH = capH;
    for (var v = 1; v <= s.n; v++) {
      var str = String(v), m = ctx.measureText(str);
      var asc = m.actualBoundingBoxAscent !== undefined ? m.actualBoundingBoxAscent : capH;
      var desc = m.actualBoundingBoxDescent !== undefined ? m.actualBoundingBoxDescent : 0;
      var l = m.actualBoundingBoxLeft !== undefined ? m.actualBoundingBoxLeft : 0;
      var r = m.actualBoundingBoxRight !== undefined ? m.actualBoundingBoxRight : m.width;
      var w = l + r;
      out.byValue[v] = { str: str, asc: asc, desc: desc, l: l, r: r, w: w };
      if (w > out.maxW) out.maxW = w;
    }
    ctx.restore();
    s.labels = out;
    return out;
  };

  // Label size rules per solid, in units of the face's inradius.
  var LABEL = { 20: { h: 0.98, w: 1.62, lower: 0.04 }, 12: { h: 0.66, w: 1.3, lower: 0 }, 8: { h: 1.0, w: 1.3, lower: 0.06 } };

  Die.prototype._drawLabel = function (ctx, f, M, T, sc, alpha, light) {
    var s = this.solid, L = this._labels(ctx), rule = LABEL[s.sides] || LABEL[20];
    var info = L.byValue[f.value];
    if (!info) return;
    var k = Math.min(rule.h * f.inr / L.capH, rule.w * f.inr / L.maxW);  // solid units per font px
    var fr = this._faceFrame(f, M, T, sc, rule.lower * f.inr);
    ctx.setTransform(fr.ax * k, fr.ay * k, -fr.bx * k, -fr.by * k, fr.ox, fr.oy);
    ctx.font = '700 ' + FONT_PX + 'px ' + FAMILY;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    var x = -(info.r - info.l) / 2, y = (info.asc - info.desc) / 2;
    var ul = (f.value === 6 || f.value === 9);
    var ulY = y + L.capH * 0.13, ulH = L.capH * 0.085, ulX = -info.w * 0.36, ulW = info.w * 0.72;
    var off = L.capH * 0.045;
    ctx.globalAlpha = alpha;
    if (this.skin === 'gold') {
      // engraved: the lower lip of the cut catches the key light
      ctx.fillStyle = rgba(C_PALE, 0.55 * (0.4 + 0.6 * light));
      ctx.fillText(info.str, x + off * 0.4, y + off);
      if (ul) ctx.fillRect(ulX + off * 0.4, ulY + off, ulW, ulH);
      ctx.fillStyle = rgb(C_INK);
      ctx.fillText(info.str, x, y);
      if (ul) ctx.fillRect(ulX, ulY, ulW, ulH);
    } else if (this.skin === 'mythic') {
      // cut deep into the glow: a white lower lip, then dark violet ink
      ctx.fillStyle = rgba([255, 250, 255], 0.6 * (0.45 + 0.55 * light));
      ctx.fillText(info.str, x + off * 0.4, y + off);
      if (ul) ctx.fillRect(ulX + off * 0.4, ulY + off, ulW, ulH);
      ctx.fillStyle = rgb(C_MYINK);
      ctx.fillText(info.str, x, y);
      if (ul) ctx.fillRect(ulX, ulY, ulW, ulH);
    } else {
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillText(info.str, x + off * 0.4, y + off);
      if (ul) ctx.fillRect(ulX + off * 0.4, ulY + off, ulW, ulH);
      var top = mix(C_HOT, [255, 240, 196], light * 0.8), bot = mix(C_DEEP, C_GOLD, 0.35 + 0.65 * light);
      var g = ctx.createLinearGradient(0, y - info.asc, 0, y + info.desc + (ul ? L.capH * 0.25 : 0));
      g.addColorStop(0, rgb(top, 0.55 + 0.5 * light));
      g.addColorStop(1, rgb(bot, 0.6 + 0.5 * light));
      ctx.fillStyle = g;
      ctx.fillText(info.str, x, y);
      if (ul) ctx.fillRect(ulX, ulY, ulW, ulH);
    }
    ctx.globalAlpha = 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  };

  Die.prototype._drawPoly = function (ctx, M, T) {
    var s = this.solid, sc = s.scale, V = s.V, F = s.F, i, k;
    var P = [], W = [];
    for (i = 0; i < V.length; i++) {
      var w = addv(mulv(mvec(M, V[i]), sc), T);
      W.push(w);
      P.push(this._project(w, [0, 0]));
    }
    var vis = [], light = [], normals = [];
    var seam = Math.max(0.6, this.dpr * 0.5);
    ctx.lineJoin = 'round';
    for (i = 0; i < F.length; i++) {
      var f = F[i], n = mvec(M, f.n);
      normals.push(n);
      var c = addv(mulv(mvec(M, f.c), sc), T);
      var vv = [-c[0], -c[1], CAM - c[2]];
      var facing = dot(n, vv);
      if (facing <= 0) { vis.push(0); light.push(0); continue; }
      var vl = len(vv), v = mulv(vv, 1 / vl);
      vis.push(facing / vl);
      light.push(Math.max(0, dot(n, LK)));
      var nA = nrm(addv(n, mulv(TILT_DIR, 0.2))), nB = nrm(sub(n, mulv(TILT_DIR, 0.2)));
      var cA = shade(this.skin, nA, v), cB = shade(this.skin, nB, v);
      var lo = 1e9, hi = -1e9, pl = null, ph = null;
      ctx.beginPath();
      for (k = 0; k < f.idx.length; k++) {
        var p = P[f.idx[k]];
        if (k === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]);
        var key = p[0] + p[1];
        if (key < lo) { lo = key; pl = p; }
        if (key > hi) { hi = key; ph = p; }
      }
      ctx.closePath();
      var g = ctx.createLinearGradient(pl[0], pl[1], ph[0], ph[1]);
      g.addColorStop(0, rgb(cA));
      g.addColorStop(1, rgb(cB));
      ctx.fillStyle = g;
      ctx.fill();
      ctx.strokeStyle = g;
      ctx.lineWidth = seam;
      ctx.stroke();
      // soft specular bloom on the facet that faces the viewer
      var fcos = facing / vl;
      if (fcos > 0.8) {
        var pc = [0, 0];
        this._project(c, pc);
        var hx = pc[0] + (pl[0] - pc[0]) * 0.42, hy = pc[1] + (pl[1] - pc[1]) * 0.42;
        var hr = Math.sqrt((pl[0] - pc[0]) * (pl[0] - pc[0]) + (pl[1] - pc[1]) * (pl[1] - pc[1])) * 1.15;
        var ha = (this.skin === 'gold' ? 0.3 : this.skin === 'mythic' ? 0.16 : 0.13) * smooth((fcos - 0.8) / 0.2);
        var hg = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr);
        hg.addColorStop(0, rgba([255, 244, 220], ha));
        hg.addColorStop(0.45, rgba([255, 236, 190], ha * 0.35));
        hg.addColorStop(1, rgba([255, 236, 190], 0));
        ctx.fillStyle = hg;
        ctx.fill();
      }
    }
    // edges: a bevel line shaded by the average of the two face normals
    var R = this.R, ew = Math.max(1, R * (this.skin === 'gold' ? 0.022 : 0.02));
    ctx.lineCap = 'round';
    for (i = 0; i < s.edges.length; i++) {
      var e = s.edges[i];
      if (!vis[e.f1] && !vis[e.f2]) continue;
      var bn = nrm(addv(normals[e.f1], normals[e.f2]));
      var mid = mulv(addv(W[e.a], W[e.b]), 0.5);
      var vv2 = nrm([-mid[0], -mid[1], CAM - mid[2]]);
      var silhouette = !(vis[e.f1] && vis[e.f2]);
      var a = P[e.a], b = P[e.b];
      if (this.skin === 'gold') {
        ctx.strokeStyle = 'rgba(40,26,6,0.45)';
        ctx.lineWidth = ew * 2.1;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
        ctx.strokeStyle = rgb(shade('gold', bn, vv2), 1.22);
        ctx.lineWidth = ew * (silhouette ? 0.9 : 1);
      } else if (this.skin === 'mythic') {
        // a thin dark cut under a bright, milky bevel: the facets read, the die still glows
        ctx.strokeStyle = rgba(C_MYINK, silhouette ? 0.22 : 0.3);
        ctx.lineWidth = ew * 2.2;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
        ctx.strokeStyle = rgb(shade('mythicTrim', bn, vv2));
        ctx.lineWidth = ew * (silhouette ? 0.9 : 1);
      } else {
        ctx.strokeStyle = rgb(shade('trim', bn, vv2), silhouette ? 0.95 : 1.05);
        ctx.lineWidth = ew * (silhouette ? 1.15 : 1);
      }
      ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
    }
    // numerals
    for (i = 0; i < F.length; i++) {
      if (!vis[i]) continue;
      var al = smooth((vis[i] - 0.1) / 0.28);
      if (al < 0.02) continue;
      this._drawLabel(ctx, F[i], M, T, sc, al, light[i]);
    }
  };

  var PIPS = {
    1: [[0, 0]],
    2: [[-1, 1], [1, -1]],
    3: [[-1, 1], [0, 0], [1, -1]],
    4: [[-1, 1], [1, 1], [-1, -1], [1, -1]],
    5: [[-1, 1], [1, 1], [0, 0], [-1, -1], [1, -1]],
    6: [[-1, 1], [-1, 0], [-1, -1], [1, 1], [1, 0], [1, -1]]
  };

  Die.prototype._drawCube = function (ctx, M, T) {
    var s = this.solid, sc = s.scale, mesh = s.mesh, i, k;
    var seam = Math.max(0.6, this.dpr * 0.5);
    ctx.lineJoin = 'round';
    var p = [0, 0];
    for (i = 0; i < mesh.quads.length; i++) {
      var q = mesh.quads[i], n = mvec(M, q.n);
      var c = addv(mulv(mvec(M, q.c), sc), T);
      var vv = [-c[0], -c[1], CAM - c[2]];
      if (dot(n, vv) <= 0) continue;
      var v = nrm(vv);
      ctx.beginPath();
      var lo = 1e9, hi = -1e9, pl = [0, 0], ph = [0, 0], QP = [];
      for (k = 0; k < 4; k++) {
        this._project(addv(mulv(mvec(M, q.p[k]), sc), T), p);
        QP.push([p[0], p[1]]);
        if (k === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]);
        var key = p[0] + p[1];
        if (key < lo) { lo = key; pl = [p[0], p[1]]; }
        if (key > hi) { hi = key; ph = [p[0], p[1]]; }
      }
      ctx.closePath();
      var fill;
      if (q.flat) {
        var nA = nrm(addv(n, mulv(TILT_DIR, 0.12))), nB = nrm(sub(n, mulv(TILT_DIR, 0.12)));
        fill = ctx.createLinearGradient(pl[0], pl[1], ph[0], ph[1]);
        fill.addColorStop(0, rgb(shade(this.skin, nA, v)));
        fill.addColorStop(1, rgb(shade(this.skin, nB, v)));
      } else {
        // smooth rounding: shade at the vertex normals, blend across the strip
        var sk = this.skin, cs = [0, 1, 2, 3].map(function (m) { return shade(sk, mvec(M, q.vn[m]), v); });
        var a0, a1, b0, b1;
        if (q.along01) { a0 = 0; a1 = 3; b0 = 1; b1 = 2; } else { a0 = 0; a1 = 1; b0 = 3; b1 = 2; }
        var gA = [(QP[a0][0] + QP[a1][0]) / 2, (QP[a0][1] + QP[a1][1]) / 2], gB = [(QP[b0][0] + QP[b1][0]) / 2, (QP[b0][1] + QP[b1][1]) / 2];
        if (Math.abs(gA[0] - gB[0]) + Math.abs(gA[1] - gB[1]) < 0.01) fill = rgb(shade(sk, n, v));
        else {
          fill = ctx.createLinearGradient(gA[0], gA[1], gB[0], gB[1]);
          fill.addColorStop(0, rgb(mix(cs[a0], cs[a1], 0.5)));
          fill.addColorStop(1, rgb(mix(cs[b0], cs[b1], 0.5)));
        }
      }
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.strokeStyle = fill;
      ctx.lineWidth = seam;
      ctx.stroke();
    }
    // pips on the flat faces
    for (i = 0; i < s.F.length; i++) {
      var f = s.F[i], fn = mvec(M, f.n);
      var fc = addv(mulv(mvec(M, f.c), sc), T);
      var fv = [-fc[0], -fc[1], CAM - fc[2]];
      var facing = dot(fn, fv) / len(fv);
      if (facing <= 0.04) continue;
      var al = smooth((facing - 0.04) / 0.25);
      var light = Math.max(0, dot(fn, LK));
      this._drawPips(ctx, f, M, T, sc, al, light);
    }
  };

  Die.prototype._drawPips = function (ctx, f, M, T, sc, alpha, light) {
    var fr = this._faceFrame(f, M, T, sc, 0);
    ctx.setTransform(fr.ax, fr.ay, -fr.bx, -fr.by, fr.ox, fr.oy);
    ctx.globalAlpha = alpha;
    var list = PIPS[f.value] || [], off = 0.47, i;
    var rad = f.value === 1 ? 0.25 : 0.155;
    for (i = 0; i < list.length; i++) {
      var x = list[i][0] * off, y = -list[i][1] * off;   // text space: y down
      if (this.skin === 'gold') {
        ctx.fillStyle = rgba(C_PALE, 0.6 * (0.4 + 0.6 * light));
        ctx.beginPath(); ctx.arc(x + rad * 0.05, y + rad * 0.13, rad, 0, TAU); ctx.fill();
        var g = ctx.createRadialGradient(x - rad * 0.25, y - rad * 0.3, rad * 0.1, x, y, rad);
        g.addColorStop(0, 'rgb(12,8,3)');
        g.addColorStop(1, rgb(C_INK, 1.5));
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(x, y, rad, 0, TAU); ctx.fill();
      } else if (this.skin === 'mythic') {
        ctx.fillStyle = rgba([255, 250, 255], 0.62 * (0.45 + 0.55 * light));
        ctx.beginPath(); ctx.arc(x + rad * 0.05, y + rad * 0.13, rad, 0, TAU); ctx.fill();
        var gm = ctx.createRadialGradient(x - rad * 0.25, y - rad * 0.3, rad * 0.1, x, y, rad);
        gm.addColorStop(0, 'rgb(14,5,24)');
        gm.addColorStop(1, rgb(C_MYINK, 1.3));
        ctx.fillStyle = gm;
        ctx.beginPath(); ctx.arc(x, y, rad, 0, TAU); ctx.fill();
      } else {
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.beginPath(); ctx.arc(x + rad * 0.05, y + rad * 0.12, rad, 0, TAU); ctx.fill();
        var g2 = ctx.createRadialGradient(x - rad * 0.35, y - rad * 0.4, rad * 0.05, x, y, rad * 1.05);
        g2.addColorStop(0, rgb(mix(C_PALE, [255, 244, 214], 0.5), 0.75 + 0.35 * light));
        g2.addColorStop(0.55, rgb(C_HOT, 0.62 + 0.45 * light));
        g2.addColorStop(1, rgb(C_DEEP, 0.9 + 0.4 * light));
        ctx.fillStyle = g2;
        ctx.beginPath(); ctx.arc(x, y, rad, 0, TAU); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  };

  // ---------------------------------------------------------------- public die methods
  Die.prototype.roll = function (result, o) {
    var self = this;
    o = o || {};
    return new Promise(function (resolve) {
      try {
        if (self._dead) { resolve(result); return; }
        var v = self._clampValue(result);
        var now = perf();
        if (self._roll) { var old = self._roll; if (old.timer) win.clearTimeout(old.timer); self._roll = null; try { old.resolve(old.value); } catch (e0) { /* ignore */ } }
        self._fx = null; self._frozen = null;
        var reduced = typeof o.reduced === 'boolean' ? o.reduced : self._reduced();
        var dur = +o.duration > 0 ? +o.duration : self.duration;
        var delay = +o.delay > 0 ? +o.delay : 0;
        var R = {
          value: v, Mf: self._poseFor(v), t0: now + delay, dur: reduced ? 160 : dur, reduced: reduced,
          resolve: resolve, onLand: typeof o.onLand === 'function' ? o.onLand : null,
          rng: typeof o.seed === 'number' ? seeded(o.seed) : self._rng, imp: 0, init: false
        };
        self._roll = R;
        self._held = false;
        if (reduced) {
          self.Mf = R.Mf; self.value = v; self._held = true;
          self.canvas.style.opacity = '0';
          self._fade = { t0: R.t0, dur: 160 };
        }
        // Safety net: finish even if frames stall (hidden tab, throttled in-app browser).
        R.timer = win.setTimeout(function () { if (self._roll === R) self._land(perf()); }, delay + R.dur + 250);
        wake(self);
      } catch (e) {
        warn('roll failed: ' + (e && e.message));
        resolve(result);
      }
    });
  };

  Die.prototype.show = function (result) {
    try {
      if (this._dead) return this;
      var v = this._clampValue(result);
      if (this._roll) { var old = this._roll; if (old.timer) win.clearTimeout(old.timer); this._roll = null; try { old.resolve(old.value); } catch (e0) { /* ignore */ } }
      this._fx = null; this._fade = null; this._frozen = null;
      this.canvas.style.opacity = '';
      this.value = v; this.Mf = this._poseFor(v); this._held = true;
      this.canvas.setAttribute('aria-label', 'd' + this.sides + ' showing ' + v);
      this._redraw();
    } catch (e) { warn('show failed: ' + (e && e.message)); }
    return this;
  };

  Die.prototype.setIdle = function (on) {
    try {
      this.idleOn = !!on;
      if (this.idleOn) { this._held = false; this._idleT0 = perf(); wake(this); }
      else this._redraw();
    } catch (e) { /* ignore */ }
    return this;
  };

  Die.prototype.setSkin = function (skin) {
    try { this.skin = skinOf(skin); this._redraw(); } catch (e) { /* ignore */ }
    return this;
  };

  Die.prototype.destroy = function () {
    if (this._dead) return;
    this._dead = true;
    try {
      if (this._roll) { var R = this._roll; if (R.timer) win.clearTimeout(R.timer); this._roll = null; try { R.resolve(R.value); } catch (e0) { /* ignore */ } }
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      if (this._onWinResize) win.removeEventListener('resize', this._onWinResize);
      var i = live.indexOf(this); if (i >= 0) live.splice(i, 1);
      var j = allDice.indexOf(this); if (j >= 0) allDice.splice(j, 1);
      if (this.wrap && this.wrap.parentNode) this.wrap.parentNode.removeChild(this.wrap);
      else if (this.ctx && !this.ownCanvas) { this.ctx.setTransform(1, 0, 0, 1, 0, 0); this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height); }
    } catch (e) { /* ignore */ }
    this.ctx = null;
  };

  // Test hooks (not public API): freeze the drawing on one moment so it can be captured.
  // _frameAt: the pose at fraction t (0..1) of a seeded throw toward `result`.
  // _fxAt: the landed `result` with its crit/fumble effect `ms` milliseconds in.
  // show() or roll() releases the freeze.
  Die.prototype._frameAt = function (result, t, seed) {
    try {
      var v = this._clampValue(result);
      this._roll = null; this._fx = null; this._held = true;
      var R = { value: v, Mf: this._poseFor(v), t0: 0, dur: 1, rng: seeded(seed || 1), imp: 0, init: false };
      this._initRoll(R, perf());
      this._frozen = { P: this._rollPose(R, clamp01(t)) };
      this._redraw();
    } catch (e) { warn('frameAt failed: ' + (e && e.message)); }
    return this;
  };
  Die.prototype._fxAt = function (result, ms, seed) {
    try {
      this.show(result);
      var v = this.value, red = this._reduced();
      var type = v === this.solid.n ? 'crit' : v === 1 ? 'fumble' : null;
      if (!type) return this;
      this._fx = { type: type, t0: 0, dur: type === 'crit' ? (red ? 700 : 900) : (red ? 450 : 560), reduced: red, seed: seed || 5 };
      this._frozen = { now: ms };
      this._redraw();
    } catch (e) { warn('fxAt failed: ' + (e && e.message)); }
    return this;
  };

  // ---------------------------------------------------------------- module API
  function create(el, o) {
    try {
      if (!el) throw new Error('no container');
      return new Die(el, o);
    } catch (e) {
      warn('create failed: ' + (e && e.message));
      var noop = function () { return this; };
      return {
        roll: function (r) { return Promise.resolve(r); }, show: noop, setIdle: noop, setSkin: noop,
        destroy: function () {}, value: 0, sides: (o && o.sides) || 20
      };
    }
  }

  function rollMany(el, specs, o) {
    o = o || {};
    try {
      if (!el || !specs || !specs.length) return Promise.resolve([]);
      var key = specs.map(function (s) { return +s.sides || 20; }).join(',') + '|' + (o.skin || '');
      var st = el.__qodMany;
      if (!st || st.key !== key || st.dice.some(function (d) { return d._dead; })) {
        if (st) st.dice.forEach(function (d) { d.destroy(); });
        if (st && st.row && st.row.parentNode) st.row.parentNode.removeChild(st.row);
        var row = doc.createElement('div');
        row.className = 'qod-row';
        row.style.cssText = 'display:flex;align-items:center;justify-content:center;width:100%;height:100%;pointer-events:none;';
        el.appendChild(row);
        var n = specs.length, cw = el.clientWidth || 240, ch = el.clientHeight || 0;
        var gapShare = 0.14;
        var S = +o.size > 0 ? +o.size : Math.floor(Math.min(ch > 0 ? ch : 1e9, cw / (n + gapShare * (n - 1)), 180));
        var dice = specs.map(function (sp, i) {
          // start on a face other than the result so a staggered die never shows its answer early
          var nf = faceCount(sp.sides) || 20, r0 = Math.round(+sp.result) || 1;
          var start = sp.value || (1 + ((r0 - 1 + 1 + Math.floor(Math.random() * (nf - 1))) % nf));
          var cell = doc.createElement('div');
          cell.style.cssText = 'flex:0 0 auto;width:' + S + 'px;height:' + S + 'px;' + (i ? 'margin-left:' + Math.round(S * gapShare) + 'px;' : '');
          row.appendChild(cell);
          return new Die(cell, {
            sides: sp.sides, skin: o.skin || sp.skin, idle: !!o.idle, reduced: o.reduced, critFx: o.critFx,
            value: start, duration: o.duration, onImpact: i === 0 ? o.onImpact : null, tilt: o.tilt, turn: o.turn
          });
        });
        st = el.__qodMany = { key: key, dice: dice, row: row };
      }
      var dice2 = st.dice;
      var p = Promise.all(specs.map(function (sp, i) {
        return dice2[i].roll(sp.result, { duration: o.duration, delay: i * (o.stagger !== undefined ? +o.stagger : 90), reduced: o.reduced });
      }));
      p.dice = dice2;
      return p;
    } catch (e) {
      warn('rollMany failed: ' + (e && e.message));
      return Promise.resolve(specs ? specs.map(function (s) { return s.result; }) : []);
    }
  }

  function clearMany(el) {
    try {
      var st = el && el.__qodMany;
      if (!st) return;
      st.dice.forEach(function (d) { d.destroy(); });
      if (st.row && st.row.parentNode) st.row.parentNode.removeChild(st.row);
      el.__qodMany = null;
    } catch (e) { /* ignore */ }
  }

  function faceCount(sides) {
    sides = +sides;
    return VALUE_SEQ[sides] ? sides : 0;
  }

  win.QODice = {
    version: VERSION,
    create: create,
    rollMany: rollMany,
    clear: clearMany,
    faceCount: faceCount,
    supported: [6, 8, 12, 20],
    _solid: getSolid   // geometry, for tests
  };
})(typeof window !== 'undefined' ? window : null);
